import { useConfirmation } from "src/sudokit/components/ConfirmationDialog/ConfirmationProvider";
import { Fragment, useState, useEffect, useRef, useMemo, useCallback } from "react";
import { DndContext, MeasuringStrategy, DragOverlay, useDroppable } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import * as Sentry from "@sentry/react";
import classNames from "classnames";
import { Container } from "./styles";
import { useEntryState } from "./useEntryState";
import Auth from "../../Auth.js";
import { invalidateStoryBibleSummaryCaches, storyBibleCacheProjectIdsForEntities } from "../../lib/storyBibleSummaryCache.js";
import WorldbuildingEntry from "../WorldbuildingEntry";
import SVG from "react-inlinesvg";
import CustomizeEntityDefaults from "../CustomizeEntityDefaults/index.js";
import { useProjectStore } from "../../stores/projectStore.js";
import { SMALL_HEADER_BREAKPOINT } from "../StoryBible/index.js";
import AddElementGuidance from "./AddElementGuidance";
import ImportWorldbuilding from "../Modals/ImportWorldbuilding";
import { useMultiSelect } from "../../hooks/useMultiSelect";
import { useStoryBibleStore } from "src/stores/storyBibleStore";
import { AppEventPublisher, StoryBibleAction, StoryBibleType } from "../../AppEventPublisher";
import ButtonSelect from "src/sudokit/components/SudoSelect/ButtonSelect.js";
import ThreeDotsSelect from "src/sudokit/components/SudoSelect/ThreeDotsSelect.js";
import GenerateElementForm from "./GenerateElementForm";
import { motion } from "framer-motion";
import SeriesTag from "../StoryBible/SeriesTag.tsx";
import { useToast } from "../ToastWrapper/index.js";
import { worldbuildingEntryToPlainText } from "../StoryBible/Utils";
import { useSudowriteStore } from "../../stores/sudowriteStore.js";
import { useSudoLayoutEventEmitter } from "src/lib/layout-events";
import { usePersistentChatStore } from "src/stores/persistentChatStore";
import { useStableCallback } from "../../hooks/useStableCallback";
import { StoryBibleEntityLoadStatus } from "../StoryBible/StoryBibleEntityLoadStatus";
import { mergeSavedUpdatedAts } from "../StoryBible/entitySync";

const COMPRESS_SERIES_BUTTON_BREAKPOINT = 600;
/**
 * Converts all worldbuilding entries to plain text, separated by double newlines
 */
function worldbuildingEntriesToPlainText(entries) {
  if (!entries || entries.length === 0) return "";

  return entries
    .map(worldbuildingEntryToPlainText)
    .filter((text) => text) // Filter out empty entry representations
    .join("\n\n");
}

const DroppableArea = ({ start, entryId }) => {
  const { attributes, setNodeRef, isOver } = useDroppable({ id: entryId, data: { start } });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      style={{
        height: "6px",
        borderRadius: "4px",
        backgroundColor: isOver ? "var(--primary-cta)" : "transparent",
        margin: "4px",
      }}
    />
  );
};

const WORLDBUILDING_INTERACTIVE_SELECTORS = [
  "p",
  "svg",
  ".hotdog-dots",
  "input",
  "div.key",
  ".role-selector",
  ".field-container",
  ".field-hints-container",
  ".field-hint-container",
  ".text-button-wrapper",
  ".actions",
  ".guidance-container",
];

export const Worldbuilding = ({ projectId, project, collapsed = false, onToggleCollapse, onSetCollapsed }) => {
  const confirmAction = useConfirmation(projectId);
  const {
    //
    addFieldsToEntities,
    createEntryFromRole,
    customDefaults,
    doneUpdatingEntityFields,
    draggingId,
    entryRoleOptions,
    endOfListRef,
    entries,
    entriesLoadFailed,
    entriesLoading,
    generatingEntry,
    guidanceRef,
    guidance,
    guidanceRoleOption,
    guidanceRoleOptions,
    setGuidance,
    handleDeleteEntries,
    handleDragCancel,
    handleDragEnd,
    handleDragStart,
    handleSaveEntry,
    handleGenerateSubmitted,
    handleRandomGenerate,
    handleGuidanceKeys,
    handleGuidanceRoleChange,
    handleRandomGenerateSimplified,
    initialLoaded,
    isAddingDefaultFieldsToEntities,
    isCustomizingDefaults,
    isCreating,
    loadEntries,
    loadCustomDefaults,
    showGenerateGuidance,
    toggleEntryCollapsed,
    toggleIsCustomizingDefaults,
    updatedEntityNames,
    restoreSnapshot,
    isRestoring,
    guidanceBottomRef,
    showGenerateGuidanceBottom,
    regenerateTrait,
    updateEntries,
    showGenerateElementForm,
    setShowGenerateElementForm,
    generateElementFormRef,
    syncing,
  } = useEntryState(projectId, project.seriesId, collapsed, onToggleCollapse, onSetCollapsed);
  const hasAccessToAgenticChat = useSudowriteStore((state) => state.subscriptionJSON?.hasAccessToAgenticChat);
  const emitLayoutEvent = useSudoLayoutEventEmitter();
  const globalCtaActive = usePersistentChatStore((state) => {
    const cta = state.ctaAction;
    return cta && cta.status === "in_progress";
  });
  const [showSuggestions, setShowSuggestions] = useState(project.showWorldBuildingSuggestions);
  const [smallWidth, setSmallWidth] = useState(false);
  const columnWidths = useProjectStore((state) => state.columnWidths);
  const [openImport, setOpenImport] = useState(false);
  const [isCustomizingWhileImporting, setIsCustomizingWhileImporting] = useState(false);
  const [isHoveredFooter, setIsHoveredFooter] = useState(false);
  const [compressSeriesButton, setCompressSeriesButton] = useState(false);
  const noElements = initialLoaded && entries.length === 0;
  const { setToast } = useToast();
  const forceRenderSearchables = useStoryBibleStore((state) => state.forceRenderSearchables);
  const expandedForSearchRef = useRef(new Set());
  const prevForceRenderSearchablesRef = useRef(forceRenderSearchables);
  const entriesRef = useRef(entries);
  const updateEntriesRef = useRef(updateEntries);

  // Keep refs in sync with latest values
  useEffect(() => {
    entriesRef.current = entries;
    updateEntriesRef.current = updateEntries;
  }, [entries, updateEntries]);

  const allCollapsed = useMemo(() => {
    return entries.length > 0 && entries.every((e) => e.collapsed);
  }, [entries]);

  const handleCollapseExpandAll = async () => {
    const shouldCollapse = !allCollapsed;
    const updatedEntries = entries.map((e) => ({ ...e, collapsed: shouldCollapse }));
    updateEntries(updatedEntries);

    try {
      const results = await Promise.allSettled(
        updatedEntries.map((entry) =>
          Auth.fetchWithIdToken(`/api/worldbuildingEntries/${entry.id}`, {
            method: "PUT",
            entry: { id: entry.id, collapsed: shouldCollapse },
            metadataOnly: true,
            updatedAt: entry.updatedAt,
            projectId,
            deferSummaryCacheInvalidation: true,
          })
        )
      );
      const savedEntries = results
        .filter((result) => result.status === "fulfilled" && result.value?.entry)
        .map((result) => result.value.entry);
      const currentEntries = entriesRef.current;
      const mergedEntries = mergeSavedUpdatedAts(currentEntries, savedEntries);
      if (mergedEntries !== currentEntries) {
        updateEntriesRef.current(mergedEntries);
      }
      if (results.some((result) => result.status === "fulfilled")) {
        await invalidateStoryBibleSummaryCaches(
          storyBibleCacheProjectIdsForEntities(updatedEntries, projectId),
          ["worldbuilding"],
        );
      }
      const failed = results.find((result) => result.status === "rejected");
      if (failed) {
        Sentry.captureException(failed.reason, { tags: { projectId } });
      }
    } catch (err) {
      Sentry.captureException(err, { tags: { projectId } });
    }
  };

  const {
    selectedItems,
    handleSelect,
    isSelected,
    clearSelection,
  } = useMultiSelect(entries, WORLDBUILDING_INTERACTIVE_SELECTORS, "worldbuilding");

  const handleShowSuggestions = async () => {
    setShowSuggestions(true);
    await project.setWorldBuildingSuggestionVisibility(true);
  };
  const onDeleteEntries = useCallback(
    async (entryIds) => {
      await handleDeleteEntries(entryIds);
      clearSelection();
    },
    [handleDeleteEntries, clearSelection],
  );

  const handleShowGenerateElementForm = (position) => {
    AppEventPublisher.storyBible.worldbuilding.openGenerate(position);
    setShowGenerateElementForm(true);
    generateElementFormRef?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => {
      generateElementFormRef?.current?.querySelector("textarea")?.focus({ preventScroll: true });
    }, 300);
  };

  useEffect(() => {
    if (columnWidths.center.current <= SMALL_HEADER_BREAKPOINT) {
      setSmallWidth(true);
    } else {
      setSmallWidth(false);
    }

    if (columnWidths.center.current <= COMPRESS_SERIES_BUTTON_BREAKPOINT) {
      setCompressSeriesButton(true);
    } else {
      setCompressSeriesButton(false);
    }
  }, [columnWidths.center]);

  // Cancel handle for in-progress batched expansion
  const expandCancelRef = useRef(null);

  // Expand collapsed entries when Find/Replace opens, restore when it closes
  // PERF: Expand in batches to avoid blocking the main thread for ~3 seconds
  useEffect(() => {
    const currentEntries = entriesRef.current;
    const currentUpdateEntries = updateEntriesRef.current;

    // Safety check: ensure entries is an array
    if (!Array.isArray(currentEntries)) return;

    const prevValue = prevForceRenderSearchablesRef.current;
    prevForceRenderSearchablesRef.current = forceRenderSearchables;

    // Only act on transitions (false -> true or true -> false)
    if (forceRenderSearchables && !prevValue) {
      // Cancel any in-progress expansion from a previous open
      if (expandCancelRef.current) expandCancelRef.current();

      // Find/Replace opened - expand all collapsed entries and track which ones were expanded
      const collapsedIds = currentEntries.filter((e) => e && e.collapsed).map((e) => e.id);
      expandedForSearchRef.current = new Set(collapsedIds);

      // PERF: Expand in batches of 10 to keep each render under ~150ms.
      // This lets the modal appear instantly and keeps typing responsive.
      if (collapsedIds.length > 0) {
        const BATCH_SIZE = 10;
        let offset = 0;
        let cancelled = false;
        expandCancelRef.current = () => { cancelled = true; };

        const expandNextBatch = () => {
          if (cancelled || offset >= collapsedIds.length) return;

          const batchIds = new Set(collapsedIds.slice(offset, offset + BATCH_SIZE));
          offset += BATCH_SIZE;

          const entries = entriesRef.current;
          const updateEntries = updateEntriesRef.current;
          if (!Array.isArray(entries)) return;
          const updated = entries.map((e) => e && batchIds.has(e.id) ? { ...e, collapsed: false } : e);
          updateEntries(updated);

          if (offset < collapsedIds.length) {
            setTimeout(expandNextBatch, 0);
          }
        };

        // Start first batch on next frame so the modal can render first
        setTimeout(expandNextBatch, 0);
      }
    } else if (!forceRenderSearchables && prevValue) {
      // Cancel any in-progress expansion
      if (expandCancelRef.current) expandCancelRef.current();
      expandCancelRef.current = null;

      // Find/Replace closed - re-collapse only the entries that were expanded for search
      const idsToCollapse = Array.from(expandedForSearchRef.current);
      if (idsToCollapse.length > 0 && currentEntries.length > 0) {
        const updatedEntries = currentEntries.map((e) =>
          e && idsToCollapse.includes(e.id) ? { ...e, collapsed: true } : e
        );
        currentUpdateEntries(updatedEntries);
        expandedForSearchRef.current.clear();
      }
    }

    // Cancel pending batched expansion on unmount
    return () => {
      if (expandCancelRef.current) expandCancelRef.current();
    };
  }, [forceRenderSearchables]);

  const handleImportWorldbuilding = () => {
    setOpenImport(true);
    AppEventPublisher.storyBible.action(StoryBibleType.worldbuilding, StoryBibleAction.Import);
  };

  const handleExportWorldbuilding = () => {
    project.exportWorldbuilding(projectId);
    AppEventPublisher.storyBible.action(StoryBibleType.worldbuilding, StoryBibleAction.Export);
  };

  const handleCopyWorldbuilding = async () => {
    if (!entries || entries.length === 0) {
      setToast("No worldbuilding to copy");
      return;
    }

    try {
      const plainText = worldbuildingEntriesToPlainText(entries);
      await navigator.clipboard.writeText(plainText);
      setToast("Worldbuilding copied");
      AppEventPublisher.storyBible.worldbuilding.copyAllWorldbuilding();
    } catch (error) {
      setToast("Oops, copying failed. Try again");
    }
  };

  const threeDotsSelectActions = useMemo(() => {
    const collapseExpandLabel = allCollapsed ? "Expand All" : "Collapse All";
    const collapseExpandIcon = allCollapsed ? "/assets/expand-all.svg" : "/assets/collapse-all.svg";
    const actions = [
      { value: "Copy Worldbuilding", label: "Copy Worldbuilding", icon: { src: "/assets/duplicate.svg", label: "Copy Worldbuilding", status: "default" } },
      { value: "Customize Traits", label: "Customize Traits", icon: { src: "/assets/gear-v2.svg", label: "Customize Traits", status: "default" } },
      { value: "Import", label: "Import", icon: { src: "/assets/import-icon.svg", label: "Import", status: "default" } },
      { value: "Export", label: "Export", icon: { src: "/assets/export.svg", label: "Export", status: "default" } },
      { value: collapseExpandLabel, label: collapseExpandLabel, icon: { src: collapseExpandIcon, label: collapseExpandLabel, status: "default" } },
    ];
    if (entries.length > 0) {
      actions.push({
        value: "Delete All",
        label: "Delete All",
        icon: { src: "/assets/delete.svg", label: "Delete All", status: "danger" },
      });
    }
    return actions;
  }, [allCollapsed, entries.length]);

  const handleDeleteAllWorldbuilding = async () => {
    if (entries.length === 0) return;
    const countLabel = entries.length === 1 ? "1 element" : `${entries.length} elements`;
    await confirmAction({
      title: "Delete all Worldbuilding",
      message: `Are you sure you want to delete all ${countLabel}? You can restore them from Trash.`,
      actionLabel: "Delete all",
      destructive: true
    }, async () => {
      await handleDeleteEntries(entries.map(entry => entry.id));
    });
  };

  const handleThreeDotsSelectAction = useStableCallback((value) => {
    switch (value) {
      case "Copy Worldbuilding":
        handleCopyWorldbuilding();
        break;
      case "Customize Traits":
        AppEventPublisher.storyBible.customizeDefaults(StoryBibleType.worldbuilding);
        toggleIsCustomizingDefaults(true);
        break;
      case "Import":
        handleImportWorldbuilding();
        break;
      case "Export":
        handleExportWorldbuilding();
        break;
      case "Collapse All":
      case "Expand All":
        handleCollapseExpandAll();
        break;
      case "Delete All":
        handleDeleteAllWorldbuilding();
        break;
      default:
        break;
    }
  });

  const addElementOptions = useMemo(
    () => [
      { value: "blank-element", label: "Blank Element", icon: { src: "/assets/story-bible/worldbuilding-icon.svg" } },
      { value: "generate", label: "Generate Element ", icon: { src: "/assets/AI-sparkle.svg" } },
      { value: "random", label: "Surprise Me", icon: { src: "/assets/dice.svg" } },
    ],
    [],
  );

  const addElementButtonProps = useMemo(
    () => ({
      variant: "tertiary",
      loadingPercentage: generatingEntry ? 100 : undefined,
      state: generatingEntry ? "loading" : "default",
      size: "small",
      leftIcon: isCreating ? "" : "/assets/add.svg",
    }),
    [generatingEntry, isCreating],
  );

  const runAddElementOptionChange = useStableCallback((position, value) => {
    onSetCollapsed(false);
    switch (value) {
      case "blank-element":
        createEntryFromRole("sudo-custom", position);
        break;
      case "generate":
        if (hasAccessToAgenticChat) {
          if (globalCtaActive) break;
          emitLayoutEvent("trigger_cta_chat", {
            type: "generate_worldbuilding",
            displayTitle: "Generate Element",
            userMessage: "Generate Element",
            cannedResponse: "Ok, let's do it!",
          });
        } else {
          handleShowGenerateElementForm(position);
        }
        break;
      case "random":
        handleRandomGenerateSimplified();
        break;
      default:
        break;
    }
  });

  const handleAddElementOptionChangeTop = useStableCallback((value) => {
    runAddElementOptionChange("top", value);
  });

  const handleAddElementOptionChangeBottom = useStableCallback((value) => {
    runAddElementOptionChange("bottom", value);
  });

  return (
    <Container className={classNames({ "space-with-worldbuilding": entries.length > 0 })}>
      <ImportWorldbuilding
        isOpen={openImport}
        setIsOpen={setOpenImport}
        projectId={projectId}
        worldbuildingEntries={entries}
        updateEntries={updateEntries}
        endOfListRef={endOfListRef}
        customDefaultFields={customDefaults}
        toggleIsCustomizingDefaultFields={toggleIsCustomizingDefaults}
        setIsCustomizingWhileImporting={setIsCustomizingWhileImporting}
      />

      <CustomizeEntityDefaults
        isOpen={isCustomizingDefaults}
        setIsOpen={toggleIsCustomizingDefaults}
        updateCurrentEntitiesWithFields={addFieldsToEntities}
        addingDefaultFields={isAddingDefaultFieldsToEntities}
        updatedEntityNames={updatedEntityNames}
        doneUpdating={doneUpdatingEntityFields}
        onDefaultsChanged={loadCustomDefaults}
        isCustomizingWhileImporting={isCustomizingWhileImporting}
        setIsCustomizingWhileImporting={setIsCustomizingWhileImporting}
        setImportIsOpen={setOpenImport}
      />
      <div className="worldbuilding-card">
        <div className="header">
          <div
            className={classNames("left-container", { "cursor-pointer": onToggleCollapse })}
            style={onToggleCollapse ? { userSelect: "none" } : undefined}
            onClick={(e) => {
              if (!onToggleCollapse) return;
              e.preventDefault();
              onToggleCollapse();
            }}
          >
            {onToggleCollapse && (
              <div className="chevron-wrapper">
                <SVG
                  src="/assets/chevron-down.svg"
                  className={classNames("collapse-chevron", { "rotated-chevron": collapsed })}
                  aria-label={`${collapsed ? "Expand" : "Collapse"} Worldbuilding`}
                />
              </div>
            )}
            <div className="left-side">
              <div className="title-lockup">
                <SVG
                  src="/assets/world.svg"
                  aria-hidden="true"
                />
                <h2 id="heading-worldbuilding">Worldbuilding</h2>
              </div>
              {!smallWidth && !collapsed && (
                <div className="about">
                  <p>Bring your world to life with Locations, Lore, Magic, and&nbsp;more</p>
                  {!showSuggestions && (
                    <div
                      className="show-suggestions-button"
                      onClick={handleShowSuggestions}
                    >
                      Show suggestions
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div
            className="right-side"
            style={smallWidth ? { marginTop: "0px" } : {}}
            onClick={(e) => e.stopPropagation()}
          >
            {project.seriesId && <SeriesTag collapsed={compressSeriesButton} />}
            <ButtonSelect
              label={generatingEntry ? "Generating" : isCreating ? "Creating..." : project.seriesId && compressSeriesButton ? "Add" : "Add Element"}
              options={addElementOptions}
              disabled={generatingEntry || isCreating}
              buttonProps={addElementButtonProps}
              onChange={handleAddElementOptionChangeTop}
            />
            <ThreeDotsSelect
              className="three-dots"
              optionAlignment="left"
              options={threeDotsSelectActions}
              onChange={handleThreeDotsSelectAction}
            />

            {showGenerateGuidance && (
              <AddElementGuidance
                containerRef={guidanceRef}
                guidanceRoleOptions={guidanceRoleOptions}
                roleSelectValue={guidanceRoleOption}
                onRoleSelectChange={handleGuidanceRoleChange}
                value={guidance}
                onChange={setGuidance}
                onKeyDown={handleGuidanceKeys}
                handleRandomGenerate={handleRandomGenerate}
                onSubmit={handleGenerateSubmitted}
                disabled={generatingEntry}
                className="section-header-position"
              />
            )}
          </div>
        </div>
      </div>

      {!collapsed && (
        <>
          <StoryBibleEntityLoadStatus
            entityLabel="worldbuilding entries"
            hasContent={entries.length > 0}
            hasError={entriesLoadFailed}
            isLoading={entriesLoading}
            onRetry={() => void loadEntries(true)}
          />
          <DndContext
            onDragCancel={handleDragCancel}
            onDragEnd={handleDragEnd}
            onDragStart={handleDragStart}
            modifiers={[restrictToVerticalAxis]}
            layoutMeasuring={{ strategy: MeasuringStrategy.Always }}
          >
            <DroppableArea
              entryId="start"
              start
            />
            {entries.map((entry, index) => {
              return (
                <Fragment key={entry.id}>
                  {index === entries.length - 1 && <div ref={endOfListRef} />}
                  <WorldbuildingEntry
                    entry={entry}
                    draggingId={draggingId}
                    handleDeleteEntries={onDeleteEntries}
                    selectedItems={selectedItems}
                    loading={syncing}
                    handleSaveEntry={handleSaveEntry}
                    toggleEntryCollapsed={toggleEntryCollapsed}
                    roleOptions={entryRoleOptions}
                    restoreSnapshot={restoreSnapshot}
                    isRestoring={isRestoring}
                    regenerateTrait={regenerateTrait}
                    isSelected={isSelected(entry.id)}
                    handleSelect={handleSelect}
                    projectTitle={entry.projectId === projectId ? project?.title : undefined}
                  />
                  <DroppableArea entryId={entry.id} />
                </Fragment>
              );
            })}
            <GenerateElementForm
              showGenerateElementForm={showGenerateElementForm}
              guidance={guidance}
              guidanceRoleOptions={guidanceRoleOptions}
              guidanceRoleOption={guidanceRoleOption}
              handleGuidanceRoleChange={handleGuidanceRoleChange}
              setGuidance={setGuidance}
              handleGenerateSubmitted={handleGenerateSubmitted}
              handleCancel={() => {
                setShowGenerateElementForm(false);
                setGuidance("");
                handleGuidanceRoleChange(guidanceRoleOptions[0]);
              }}
              generatingElement={generatingEntry}
              ref={generateElementFormRef}
              onKeyDown={handleGuidanceKeys}
            />
            <DragOverlay>
              {draggingId ? (
                <WorldbuildingEntry
                  entry={entries.find((e) => e.id === draggingId)}
                  draggingId={draggingId}
                  roleOptions={entryRoleOptions}
                />
              ) : null}
            </DragOverlay>
          </DndContext>
          {!noElements && (
            <div
              className="hoverable-footer"
              onMouseEnter={() => setIsHoveredFooter(true)}
              onMouseLeave={() => setIsHoveredFooter(false)}
            >
              {isHoveredFooter && !showGenerateElementForm && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{
                    height: { duration: 0.2, ease: "easeInOut" },
                    opacity: { duration: 0.2, ease: "easeInOut" },
                  }}
                  className="flex gap-2 justify-center"
                  style={{ overflow: "hidden" }}
                >
                  <ButtonSelect
                    label={generatingEntry ? "Generating" : isCreating ? "Creating..." : "Add Element"}
                    options={addElementOptions}
                    disabled={generatingEntry || isCreating}
                    buttonProps={addElementButtonProps}
                    onChange={handleAddElementOptionChangeBottom}
                  />
                </motion.div>
              )}
            </div>
          )}
          {showGenerateGuidanceBottom && (
            <AddElementGuidance
              containerRef={guidanceBottomRef}
              guidanceRoleOptions={guidanceRoleOptions}
              roleSelectValue={guidanceRoleOption}
              onRoleSelectChange={handleGuidanceRoleChange}
              value={guidance}
              onChange={setGuidance}
              onKeyDown={handleGuidanceKeys}
              handleRandomGenerate={handleRandomGenerate}
              onSubmit={handleGenerateSubmitted}
              disabled={generatingEntry}
            />
          )}
        </>
      )}
    </Container>
  );
};

export default Worldbuilding;
